import {ACCOUNT_DELETION_PENDING_MESSAGE} from '@/api/account-deletion';
import {ApiClientError} from '@/api/client';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {OrDivider} from '@/components/auth/OrDivider';
import {PasswordInput} from '@/components/auth/PasswordInput';
import {ProviderButtonRow} from '@/components/auth/ProviderButtonRow';
import {TribeTaleMark} from '@/components/auth/TribeTaleMark';
import {authApi} from '@/api/auth';
import {getSafeReturnTo} from '@/lib/auth-return';
import {useAuth} from '@/contexts/AuthContext';
import {useForm} from 'react-hook-form';
import {useState} from 'react';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';

const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required')
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {login} = useAuth();
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const returnTo = getSafeReturnTo(searchParams.get('returnTo'));

    const {
        register,
        handleSubmit,
        formState: {errors}
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema)
    });

    const onSubmit = async (data: LoginFormData) => {
        setError('');
        setIsLoading(true);

        try {
            await login(data.email, data.password);
            navigate(returnTo);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleProviderSelect = async (id: string) => {
        try {
            localStorage.setItem('tt:last-provider', id);
            const provider = id === 'microsoft' ? 'microsoft-entra-id' : id;
            if (provider === 'google' || provider === 'microsoft-entra-id') {
                await authApi.startSocialSignIn(provider, returnTo);
            }
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        }
    };

    return (
        <div style={{width: '100%'}}>
            {searchParams.get('deletionPending') === '1' && <p role="status">{ACCOUNT_DELETION_PENDING_MESSAGE}</p>}
            {searchParams.get('deleted') === '1' && <p role="status">Your account has been deleted.</p>}
            <header style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <TribeTaleMark size={56} />
                <h1
                    style={{
                        fontSize: 30,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: 'var(--tt-text-primary)',
                        textAlign: 'center',
                        margin: '22px 0 40px',
                        padding: 0
                    }}
                >
                    Sign in or join with Google or Microsoft
                </h1>
            </header>

            <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div style={{marginBottom: '1.5rem'}}>
                    <label
                        htmlFor="email"
                        style={{
                            display: 'block',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--tt-text-primary)',
                            marginBottom: 7
                        }}
                    >
                        Work email
                    </label>
                    <input
                        {...register('email')}
                        autoCapitalize="off"
                        autoComplete="username"
                        autoCorrect="off"
                        id="email"
                        placeholder="name@company.com"
                        style={{
                            width: '100%',
                            height: 52,
                            padding: '0 16px',
                            borderRadius: 10,
                            fontSize: 15,
                            background: 'var(--tt-input-bg)',
                            border: '1px solid var(--tt-border)',
                            color: 'var(--tt-text-primary)',
                            outline: 'none',
                            transition: 'border-color .15s linear, box-shadow .15s linear',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit'
                        }}
                        type="email"
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--tt-border)';
                            e.target.style.boxShadow = 'none';
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--tt-accent)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
                        }}
                    />
                    <div style={{fontSize: 13, color: 'var(--tt-text-secondary)', marginTop: 9}}>
                        Use an organization email to easily collaborate with teammates
                    </div>
                    {errors.email && (
                        <p style={{marginTop: 4, fontSize: '1.3rem', color: 'var(--tt-red)'}}>{errors.email.message}</p>
                    )}
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                    <label
                        htmlFor="password"
                        style={{
                            display: 'block',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--tt-text-primary)',
                            marginBottom: 7
                        }}
                    >
                        Password
                    </label>
                    <PasswordInput
                        {...register('password')}
                        autoComplete="current-password"
                        autoCorrect="off"
                        iconColor="var(--tt-text-secondary)"
                        id="password"
                        placeholder="*********"
                        rightInset={72}
                        style={{
                            width: '100%',
                            height: 52,
                            padding: '0 16px',
                            borderRadius: 10,
                            fontSize: 15,
                            background: 'var(--tt-input-bg)',
                            border: '1px solid var(--tt-border)',
                            color: 'var(--tt-text-primary)',
                            outline: 'none',
                            transition: 'border-color .15s linear, box-shadow .15s linear',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit'
                        }}
                        trailing={<Link
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 16px',
                                fontSize: 14,
                                color: 'var(--tt-text-secondary)',
                                textDecoration: 'none',
                                transition: 'color .15s linear'
                            }}
                            tabIndex={-1}
                            to={`/forgot-password?returnTo=${encodeURIComponent(returnTo)}`}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--tt-text-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--tt-text-secondary)';
                            }}
                        >
                            Forgot?
                        </Link>}
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--tt-border)';
                            e.target.style.boxShadow = 'none';
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--tt-accent)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
                        }}
                    />
                    {errors.password && (
                        <p style={{marginTop: 4, fontSize: '1.3rem', color: 'var(--tt-red)'}}>{errors.password.message}</p>
                    )}
                </div>

                <button
                    disabled={isLoading}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 52,
                        borderRadius: 10,
                        border: 'none',
                        background: 'var(--tt-accent)',
                        color: '#fff',
                        fontSize: 15.5,
                        fontWeight: 700,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                        transition: 'background-color 0.2s ease',
                        fontFamily: 'inherit'
                    }}
                    type="submit"
                    onMouseEnter={(e) => {
                        if (!isLoading) {
                            (e.target as HTMLButtonElement).style.backgroundColor = 'var(--tt-accent-hover)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isLoading) {
                            (e.target as HTMLButtonElement).style.backgroundColor = 'var(--tt-accent)';
                        }
                    }}
                >
                    {isLoading ? (
                        <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8}}>
                            <svg style={{animation: 'spin 1s linear infinite', width: 18, height: 18}} viewBox="0 0 24 24">
                                <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="4" style={{opacity: 0.25}} />
                                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor" style={{opacity: 0.75}} />
                            </svg>
                            Signing in...
                        </span>
                    ) : (
                        <>Sign in</>
                    )}
                </button>
            </form>

            {error && (
                <p
                    style={{
                        marginTop: 16,
                        color: 'var(--tt-red)',
                        fontSize: '1.4rem',
                        lineHeight: 1.5,
                        textAlign: 'center',
                        textWrap: 'balance'
                    }}
                >
                    {error}
                </p>
            )}

            <OrDivider />
            <ProviderButtonRow onSelect={handleProviderSelect} />

            <div style={{
                textAlign: 'center',
                fontSize: 12.5,
                color: 'var(--tt-text-secondary)',
                lineHeight: 1.55,
                marginTop: 30
            }}>
                By continuing, you acknowledge that you understand and agree to the<br />
                <a href="https://tribetale.com/terms" style={{color: 'var(--tt-text-primary)', textDecoration: 'underline'}}>Terms &amp; Conditions</a> and <a href="https://tribetale.com/privacy" style={{color: 'var(--tt-text-primary)', textDecoration: 'underline'}}>Privacy Policy</a>
            </div>

        </div>
    );
}
